import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { Filter, RecentChange, Status, StorageRegime, StorageRegimeService, translateCommonErrorStatus } from '@core/data';

export interface StorageRegimeStateModel {
    status: Status;
    orgKey: string;
    regimes: StorageRegime[];
    recentChanges: RecentChange[];
    recentChangesStatus: Status;
}

const DEFAULTS: StorageRegimeStateModel = {
    status: Status.UNINITIALIZED,
    orgKey: null,
    regimes: [],
    recentChanges: [],
    recentChangesStatus: Status.UNINITIALIZED,
}

export class InitStorageRegimeState {
    static readonly type = '[StorageRegimeState] Init';
    constructor(public orgKey: string) {}
}

export class LoadStorageRegimes {
    static readonly type = '[StorageRegimeState] Load';
    constructor(public orgKey: string) {}
}

export class LoadRecentChanges {
    static readonly type = '[StorageRegimeState] Load Recent Changes';
    constructor(public orgKey: string, public filter: Filter) {}
}

export class CreateCustomRegime {
    static readonly type = '[StorageRegimeState] Create';
    constructor(public orgKey: string, public regime: Partial<StorageRegime>) {}
}

export class UpdateCustomRegime {
    static readonly type = '[StorageRegimeState] Update';
    constructor(public key: string, public regime: Partial<StorageRegime>) {}
}

export class DeleteCustomRegime {
    static readonly type = '[StorageRegimeState] Delete';
    constructor(public regime: StorageRegime) {}
}

@State<StorageRegimeStateModel>({
    name: 'storage_regime_state',
    defaults: DEFAULTS,
})
@Injectable()
export class StorageRegimeState {

    @Selector()
    static regimes(state: StorageRegimeStateModel) {
        return state.regimes;
    }

    @Selector()
    static status(state: StorageRegimeStateModel) {
        return state.status;
    }

    @Selector()
    static recentChanges(state: StorageRegimeStateModel) {
        return state.recentChanges;
    }

    @Selector()
    static recentChangesStatus(state: StorageRegimeStateModel) {
        return state.recentChangesStatus;
    }

    constructor(
        private _storageRegimeService: StorageRegimeService,
    ) {}

    @Action(InitStorageRegimeState, { cancelUncompleted: true })
    init(ctx: StateContext<StorageRegimeStateModel>, action: InitStorageRegimeState) {
        ctx.setState({
            ...DEFAULTS,
            orgKey: action.orgKey,
        });

        return ctx.dispatch(new LoadStorageRegimes(action.orgKey));
    }

    @Action(LoadStorageRegimes, { cancelUncompleted: true })
    load(ctx: StateContext<StorageRegimeStateModel>, action: LoadStorageRegimes) {
        ctx.patchState({ status: Status.LOADING, orgKey: action.orgKey });

        return this._storageRegimeService.list(action.orgKey).pipe(
            tap(
                regimes => ctx.patchState({ status: Status.OK, regimes }),
                error => ctx.patchState({ status: translateCommonErrorStatus(error) }),
            ),
        );
    }

    @Action(LoadRecentChanges, { cancelUncompleted: true })
    loadRecentChanges(ctx: StateContext<StorageRegimeStateModel>, action: LoadRecentChanges) {
        // Clears previous rows so an earlier open is never shown while this one loads.
        ctx.patchState({ recentChanges: [], recentChangesStatus: Status.LOADING });

        return this._storageRegimeService.listRecentChanges(action.orgKey, action.filter).pipe(
            tap(
                recentChanges => ctx.patchState({ recentChanges, recentChangesStatus: Status.OK }),
                error => ctx.patchState({ recentChangesStatus: translateCommonErrorStatus(error) }),
            ),
        );
    }

    // On success, the new regime is appended to state.regimes directly so
    // it's immediately available for attachment without a second
    // round-trip LoadStorageRegimes. `status` is deliberately left
    // untouched here — it reflects the list-load status only; a failed
    // mutation is surfaced by the caller (the dispatching component),
    // which can show a one-off error message without leaving the whole
    // state stuck in an error status after one bad save.
    @Action(CreateCustomRegime)
    create(ctx: StateContext<StorageRegimeStateModel>, action: CreateCustomRegime) {
        return this._storageRegimeService.create(action.regime).pipe(
            tap(created => {
                const state = ctx.getState();
                ctx.patchState({ regimes: [...state.regimes, created] });
            }),
        );
    }

    @Action(UpdateCustomRegime)
    update(ctx: StateContext<StorageRegimeStateModel>, action: UpdateCustomRegime) {
        return this._storageRegimeService.update(action.key, action.regime).pipe(
            tap(updated => {
                const state = ctx.getState();
                ctx.patchState({
                    regimes: state.regimes.map(regime => regime.key === updated.key ? updated : regime),
                });
            }),
        );
    }

    @Action(DeleteCustomRegime)
    delete(ctx: StateContext<StorageRegimeStateModel>, action: DeleteCustomRegime) {
        return this._storageRegimeService.delete(action.regime.key).pipe(
            tap(() => {
                const state = ctx.getState();
                ctx.patchState({
                    regimes: state.regimes.filter(regime => regime.key !== action.regime.key),
                });
            }),
        );
    }
}
